/**
 * Polyfill for `navigator.connection` on platforms that don't implement the
 * Network Information API (notably iOS WKWebView, which backs the Capacitor
 * native WebView).
 *
 * `@oneblink/apps`' `offlineService.isOffline()` reads
 * `window.navigator.connection.type` whenever `window.cordova` is present,
 * assuming the Cordova network-information plugin has populated it. On iOS that
 * object is `undefined`, so the unguarded `.type` access throws a startup
 * `TypeError` and blanks the screen. Android's Chromium WebView implements the
 * API natively, which is why this only surfaces on iOS.
 *
 * We only need to satisfy oneblink's `type === 'none'` check, so a minimal shim
 * backed by `navigator.onLine` is sufficient. We define it only when the API is
 * genuinely missing, so real implementations (desktop Chrome, Android WebView)
 * are left untouched. `@capacitor/network`'s native plugin is used on-device and
 * does not rely on this; its web fallback reads `connection.type`, which the
 * shim also satisfies.
 *
 * This module must be imported before any code that reads `navigator.connection`.
 */
// `navigator.connection` and its vendor-prefixed variants aren't in the TS DOM
// `Navigator` type, so access them through an untyped view.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const nav = navigator as any;

if (
  typeof navigator !== "undefined" &&
  !nav.connection &&
  !nav.mozConnection &&
  !nav.webkitConnection
) {
  try {
    Object.defineProperty(navigator, "connection", {
      configurable: true,
      get() {
        return {
          // oneblink checks `type === 'none'`; capacitor maps 'wifi' -> 'wifi'.
          get type() {
            return navigator.onLine ? "wifi" : "none";
          },
        };
      },
    });
  } catch {
    // If the property can't be (re)defined on this platform, leave it alone —
    // the environment already provides something usable.
  }
}

export {};
